import {useCallback, useEffect, useState} from "react";
import {
    Button,
    LegacyCard, EmptyState,
    Form,
    FormLayout,
    Layout,
    ResourceItem,
    ResourceList,
    TextField,
} from "@shopify/polaris";
import Paginator from "../../Paginator";
import {useToast} from "@shopify/app-bridge-react";
import {EmptyStateWithSpinner} from "../../Helpers";

export default ({
                    loading = false,
                    loadResources = (query) => {},
                    cardTitle = '',
                    searchFieldLabel = '',
                    resourceName = { singular: '', plural: '' },
                    actionLabel = 'Add',
                    onAction = (resource) => {},
                    renderItem = (item) => {},
                    renderCounter = 1,
                    hidePagination = false,
                    autoLoad = false,
                    forceSearchLength = false,
})  => {
    const [query, setQuery] = useState('');
    const [internalLoading, setInternalLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState({currentPage: 1, lastPage: 1})
    const toast = useToast();

    // This ensures that the list will be rerendered when the counter changes. It's useful here,
    // because we usually have two separate instance, and when one instance
    // changes we need to refresh the other one too
    useEffect(() => {
        if (autoLoad) {
            loadData();
        }
    }, [renderCounter]);

    const loadData = (page = 1, search = null) => {
        if (!(search ?? query)?.length && forceSearchLength) {
            toast.show("You need to provide at least one letter to search.", {
                duration: 5000,
            });
            return;
        }

        setInternalLoading(true);

        const params = {
            query: search ?? query,
        };

        if (!hidePagination) {
            params.page = page;
        }

        loadResources(params)
            .then(({data}) => {
                setResults(data.data);

                if (!hidePagination) {
                    setMeta({
                        currentPage: data.current_page ?? 1,
                        lastPage: data.last_page ?? 1
                    });
                }
            })
            .finally(() => setInternalLoading(false));
    }

    const clearSearch = useCallback(() => {
        setQuery('');

        if (forceSearchLength) {
            setResults([]);
            setMeta({
                currentPage: 1,
                lastPage: 1
            });
        } else {
            loadData(1, '');
        }

    }, []);

    return (
        <Layout>
            <Layout.Section>
                <LegacyCard sectioned title={cardTitle}>
                    <Form onSubmit={() => loadData()}>
                        <FormLayout>
                            <TextField
                                value={query}
                                autoComplete={'on'}
                                onChange={setQuery}
                                label={searchFieldLabel}
                                type="text"
                                connectedRight={<Button submit  variant="primary">Search</Button>}
                                clearButton
                                onClearButtonClick={clearSearch}
                            />
                        </FormLayout>
                    </Form>
                </LegacyCard>
            </Layout.Section>

            <Layout.Section>
                <LegacyCard title="Search Results">
                    <ResourceList
                        resourceName={resourceName}
                        items={results}
                        loading={results.length > 0 && (internalLoading || loading)}
                        emptyState={<EmptyStateWithSpinner loading={internalLoading || loading} />}
                        renderItem={(item) => {
                            return (
                                <ResourceItem
                                    id={item.id}
                                    shortcutActions={[
                                        {
                                            content: actionLabel,
                                            onAction() {
                                                onAction(item)
                                            }
                                        }
                                    ]}
                                >
                                    {renderItem(item)}
                                </ResourceItem>
                            );
                        }}
                    />
                </LegacyCard>
                { !hidePagination && results.length > 0 && <Paginator currentPage={meta.currentPage} lastPage={meta.lastPage} show={true} callback={(page) => loadData(page)} /> }
            </Layout.Section>
        </Layout>
    );
}
